import React, { useEffect, useRef, useState } from "react";
import ReactModal from "react-modal";
import { toast } from "react-toastify";
import UpdateHooks from "../../Hooks/UpdateHooks";
import { FaChevronDown } from "react-icons/fa";
import { MdClose } from "react-icons/md";

const SwitchRefModal = ({ resellerData, isOpen, closeModal }) => {
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isOpenSelect, setIsOpenSelect] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedOption, setSelectedOption] = useState(null);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  const filteredOptions = doctors.filter((option) =>
    option.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    const fetchDoctors = async () => {
      try {
        const response = await fetch(
          `https://servercareseebd.careseebd.com/api/v1/user/specific?fieldName=${"role"}&&fieldValue=${"doctor"}`,
          {
            headers: {
              authorization: `Bearer ${localStorage.getItem("careSeeAccessToken")}`,
            },
          }
        );
        const data = await response.json();
        setDoctors(data?.data);
      } catch (error) {
        console.error("Error fetching doctors:", error);
      }
    };
    fetchDoctors();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpenSelect(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpenSelect && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpenSelect]);

  const handleToggle = () => {
    setIsOpenSelect((prev) => !prev);
    if (isOpenSelect) setSearchTerm("");
  };

  const handleSelect = (option) => {
    setSelectedOption(option);
    setIsOpenSelect(false);
    setSearchTerm("");
  };

  const handleClear = () => {
    setSelectedOption(null);
    setSearchTerm("");
  };

  const handelUpdate = async (e) => {
    e.preventDefault();
    if (!selectedOption) return toast.error("Please select a doctor.");

    setLoading(true);
    try {
      await UpdateHooks(
        `https://servercareseebd.careseebd.com/api/v1/user/${resellerData?._id}`,
        {
          referralId: selectedOption?._id,
          referralName: selectedOption?.name,
          referralEmail: selectedOption?.email,
          referralPhone: selectedOption?.phone,
        }
      );
      toast.success("User assigned to doctor successfully!");
      closeModal();
    } catch (error) {
      toast.error("Failed to assign doctor.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={closeModal}
      className="fixed z-50 inset-0 flex items-center justify-center p-4"
      overlayClassName="fixed inset-0 z-40"
      style={{
        overlay: {
          backgroundColor: "rgba(15, 23, 42, 0.6)",
          backdropFilter: "blur(6px)",
          WebkitBackdropFilter: "blur(6px)",
        },
        content: {
          border: "none",
          background: "transparent",
          padding: 0,
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        },
      }}
    >
      <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Header gradient bar */}
        <div className="h-1.5 w-full bg-gradient-to-r from-amber-400 via-orange-500 to-rose-500" />

        <div className="px-8 pt-7 pb-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-bold text-gray-900 tracking-tight">
                Assign to Doctor
              </h2>
              <p className="text-sm text-gray-400 mt-0.5">
                {resellerData?.name
                  ? `Assigning ${resellerData.name}`
                  : "Select a doctor to assign this user"}
              </p>
            </div>
            <button
              type="button"
              onClick={closeModal}
              className="flex items-center justify-center w-9 h-9 rounded-xl text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all duration-200"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Searchable dropdown */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
              Select Doctor
            </label>

            <div className="relative" ref={dropdownRef}>
              <button
                type="button"
                onClick={handleToggle}
                className="w-full px-4 py-2.5 text-left bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-orange-400 focus:ring-4 focus:ring-orange-400/10 hover:border-gray-300 transition-all duration-200"
              >
                <div className="flex items-center justify-between">
                  <span
                    className={
                      selectedOption ? "text-gray-800 font-medium" : "text-gray-400"
                    }
                  >
                    {selectedOption ? selectedOption.name : "Search and select a doctor..."}
                  </span>
                  <div className="flex items-center gap-1.5">
                    {selectedOption && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleClear();
                        }}
                        className="p-1 hover:bg-gray-200 rounded-full transition-colors"
                      >
                        <MdClose size={14} className="text-gray-400" />
                      </button>
                    )}
                    <FaChevronDown
                      size={12}
                      className={`text-gray-400 transition-transform duration-200 ${isOpenSelect ? "rotate-180" : ""
                        }`}
                    />
                  </div>
                </div>
              </button>

              {isOpenSelect && (
                <div className="absolute z-20 w-full mt-1.5 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden">
                  {/* Search */}
                  <div className="p-2.5 border-b border-gray-100">
                    <div className="relative">
                      <svg
                        className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z"
                        />
                      </svg>
                      <input
                        ref={searchInputRef}
                        type="text"
                        placeholder="Search doctors..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-9 pr-4 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-400/10 transition-all"
                      />
                    </div>
                  </div>

                  {/* Options */}
                  <div className="max-h-44 overflow-y-auto">
                    {filteredOptions.length > 0 ? (
                      filteredOptions.map((option) => (
                        <button
                          key={option._id}
                          type="button"
                          onClick={() => handleSelect(option)}
                          className={`w-full px-4 py-2.5 text-left text-sm transition-colors ${selectedOption?._id === option._id
                            ? "bg-orange-50 text-orange-700 font-medium"
                            : "text-gray-700 hover:bg-gray-50"
                            }`}
                        >
                          {option.name}
                        </button>
                      ))
                    ) : (
                      <p className="px-4 py-4 text-sm text-gray-400 text-center">
                        No doctors found
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Selected doctor card */}
          {selectedOption && (
            <div className="mt-4 p-4 bg-orange-50 border border-orange-100 rounded-xl">
              <p className="text-xs font-semibold text-orange-600 uppercase tracking-wider mb-2">
                Doctor Details
              </p>
              <div className="space-y-1">
                <p className="text-sm font-semibold text-gray-800">
                  {selectedOption.name}
                </p>
                <p className="text-sm text-gray-500">{selectedOption.email}</p>
                <p className="text-sm text-gray-500">{selectedOption.phone}</p>
                <p className="text-xs text-gray-400 font-mono mt-1">
                  ID: {selectedOption._id}
                </p>
              </div>
            </div>
          )}

          {/* Submit button */}
          <div className="mt-6">
            <button
              type="button"
              onClick={handelUpdate}
              disabled={loading || !selectedOption}
              className="w-full py-3 px-6 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white text-sm font-semibold rounded-xl shadow-lg shadow-orange-500/25 hover:shadow-orange-500/40 transition-all duration-200 hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg
                    className="animate-spin h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                  Assigning...
                </span>
              ) : (
                "Assign Doctor"
              )}
            </button>
          </div>
        </div>
      </div>
    </ReactModal>
  );
};

export default SwitchRefModal;
